import { useRef, useState, FormEvent } from "react";
import { CalendarDays, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import PlanUpload, { Uploaded } from "./PlanUpload";
import { api, apiError } from "@/lib/api";
import { getUtm, track } from "@/lib/track";
import { BRAND, LOCATIONS, WORK_TYPES } from "@/data/site";

type Props = { defaultWorkType?: string; defaultLocation?: string };

const EMPTY = { name: "", company: "", phone: "", email: "", address: "", location: "", work_type: "", area_sqm: "", message: "", website: "" };

export default function QuoteForm({ defaultWorkType = "", defaultLocation = "" }: Props) {
  const [f, setF] = useState({ ...EMPTY, work_type: defaultWorkType, location: defaultLocation });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [files, setFiles] = useState<Uploaded[]>([]);
  const [visit, setVisit] = useState({ on: false, date: "", slot: "" });
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const started = useRef(Date.now());
  const set = (k: keyof typeof EMPTY) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (f.name.trim().length < 2) return setError("Please enter your name.");
    if (!f.phone.trim() && !f.email.trim()) return setError("Please provide a phone number or email so we can reach you.");
    if (!f.work_type) return setError("Please select the type of work required.");
    if (visit.on && !visit.date) return setError("Please choose a preferred site visit date.");
    setBusy(true);
    try {
      await api.post("/leads", {
        ...f, source_page: window.location.pathname, utm: getUtm(), elapsed_ms: Date.now() - started.current,
        file_ids: files.map((x) => x.id), site_visit_requested: visit.on, site_visit_date: visit.date, site_visit_slot: visit.slot,
      });
      track("generate_lead", { work_type: f.work_type, location: f.location, site_visit: visit.on, plans: files.length });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div data-testid="quote-form-success" className="flex flex-col items-start gap-4 border border-brand-500/40 bg-brand-500/5 p-8">
        <CheckCircle2 className="h-10 w-10 text-brand-500" />
        <h3 className="text-3xl font-bold uppercase text-white">Enquiry received</h3>
        <p className="text-slate-300">An estimator will contact you within one business day. Need it sooner? Call us now.</p>
        <a href={BRAND.phoneHref} className="btn-brand" data-testid="quote-success-call">Call {BRAND.phone}</a>
      </div>
    );

  return (
    <form onSubmit={submit} noValidate data-testid="quote-form" className="grid gap-3 sm:grid-cols-2">
      <input className="field" placeholder="Full name *" autoComplete="name" value={f.name} onChange={set("name")} data-testid="quote-name-input" />
      <input className="field" placeholder="Company (optional)" autoComplete="organization" value={f.company} onChange={set("company")} data-testid="quote-company-input" />
      <input className="field" placeholder="Phone *" type="tel" inputMode="tel" autoComplete="tel" value={f.phone} onChange={set("phone")} data-testid="quote-phone-input" />
      <input className="field" placeholder="Email" type="email" autoComplete="email" value={f.email} onChange={set("email")} data-testid="quote-email-input" />
      <input className="field sm:col-span-2" placeholder="Site address" autoComplete="street-address" value={f.address} onChange={set("address")} data-testid="quote-address-input" />
      <select className="field" value={f.work_type} onChange={set("work_type")} data-testid="quote-worktype-select">
        <option value="">Type of work required *</option>
        {WORK_TYPES.map((w) => <option key={w} value={w}>{w}</option>)}
      </select>
      <input className="field" list="quote-location-options" placeholder="Suburb / area (type or pick)" autoComplete="address-level2" maxLength={80}
        value={f.location} onChange={set("location")} data-testid="quote-location-input" />
      <datalist id="quote-location-options">
        {LOCATIONS.map((l) => <option key={l} value={l} />)}
      </datalist>
      <input className="field" placeholder="Approx. area (m²)" inputMode="numeric" value={f.area_sqm} onChange={set("area_sqm")} data-testid="quote-area-input" />
      <textarea className="field min-h-[48px] sm:row-span-1" rows={1} placeholder="Project details / timing" value={f.message} onChange={set("message")} data-testid="quote-message-input" />
      <PlanUpload files={files} onChange={setFiles} />
      <div className="sm:col-span-2">
        <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-200">
          <input type="checkbox" checked={visit.on} onChange={(e) => setVisit({ ...visit, on: e.target.checked })} className="h-5 w-5 accent-[#139AF0]" data-testid="quote-site-visit-toggle" />
          <CalendarDays className="h-4 w-4 text-brand-500" /> Book a free site visit
        </label>
        {visit.on && (
          <div className="mt-3 grid grid-cols-[1fr_auto] gap-3" data-testid="quote-site-visit-fields">
            <input type="date" min={tomorrow} className="field [color-scheme:dark]" value={visit.date} onChange={(e) => setVisit({ ...visit, date: e.target.value })} data-testid="quote-site-visit-date" />
            <select className="field" value={visit.slot} onChange={(e) => setVisit({ ...visit, slot: e.target.value })} data-testid="quote-site-visit-slot">
              <option value="">Any time</option>
              <option value="AM">Morning (AM)</option>
              <option value="PM">Afternoon (PM)</option>
            </select>
          </div>
        )}
      </div>
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <label>Website<input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} name="website" data-testid="quote-hp-input" /></label>
      </div>
      {error && <p data-testid="quote-form-error" className="text-sm text-red-400 sm:col-span-2">{error}</p>}
      <button type="submit" disabled={busy} className="btn-brand sm:col-span-2" data-testid="quote-form-submit-button">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {busy ? "Sending" : "Request My Quote"}
      </button>
      <p className="flex items-center gap-2 text-xs text-slate-500 sm:col-span-2">
        <ShieldCheck className="h-4 w-4" /> Phone or email required. We reply within one business day — no spam, no call centres.
      </p>
    </form>
  );
}
